import { Conditional } from '@/lib/components/core/conditional/conditional';
import { Form, InputNumber, Input, Typography, Row, Col } from 'antd';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../../page.module.scss';
import { Lozingsvorm, Relatie } from '@/lib/types/relatie';
import { useGroundWaterRules } from '../use-ground-water-rules';
import { GroundWaterInfo } from '../ground-water-info';

interface LozingenInputDto {
  betrekkingTotTeeltwijze: number;
  lozingen: {
    periode: number;
    hoeveelheid?: number;
    toegediendVoedingswaterHoeveelheid?: number;
    hergebruiktDrainagewaterHoeveelheid?: number;
  }[];
}

interface Properties {
  teeltwijze: number;
}

const VolumeTableContainer: FC<{ usesRoutes: boolean; children: ReactNode }> = ({ usesRoutes, children }) =>
  usesRoutes ? (
    <>{children}</>
  ) : (
    <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
      <div style={{ minWidth: 600 }}>{children}</div>
    </div>
  );

export const LozingenV2: FC<Properties> = ({ teeltwijze }) => {
  const { t } = useTranslation('input');
  const { Title } = Typography;
  const form = Form.useFormInstance<Relatie>();
  const { ground, dependencies, dischargeValidator, reuseValidator, onDischargeBlur, onReuseBlur } = useGroundWaterRules(teeltwijze);
  const registratiejaar = Form.useWatch('registratiejaar', { form, preserve: true });
  const lozingsVormen = Form.useWatch('lozingsVorm', { form, preserve: true });
  const lozingsvorm = lozingsVormen?.find(item => item?.betrekkingTotTeeltwijze === teeltwijze)?.value ?? Lozingsvorm.GeenLozing;
  const usesRoutes = Number(registratiejaar) >= 2027;
  const volumeUnit = Number(registratiejaar) >= 2026 && !usesRoutes ? ' (m³)' : '';
  const groundColumnWidth = usesRoutes ? 100 : 160;
  const volumeColumns: { field: 'hoeveelheid' | 'hoeveelheidRiool' | 'hoeveelheidOppervlaktewater'; title: string }[] = usesRoutes
    ? [
        ...(teeltwijze === 1 && [Lozingsvorm.Riool, Lozingsvorm.RioolEnOppervlaktewater].includes(lozingsvorm)
          ? [{ field: 'hoeveelheidRiool' as const, title: 'Lozing riool hoeveelheid (m³)' }]
          : []),
        ...(teeltwijze === 0 || [Lozingsvorm.Oppervlaktewater, Lozingsvorm.RioolEnOppervlaktewater, Lozingsvorm.GeenDrainagesysteem].includes(lozingsvorm)
          ? [{ field: 'hoeveelheidOppervlaktewater' as const, title: teeltwijze === 0 ? 'Lozing hoeveelheid (m³)' : 'Lozing oppervlaktewater hoeveelheid (m³)' }]
          : []),
      ]
    : [{ field: 'hoeveelheid', title: 'Lozing hoeveelheid' }];

  const [lozingenList, setLozingenList] = useState<LozingenInputDto[]>([]);

  useEffect(() => {
    const existingLozingen = form.getFieldValue('lozingen') || [];

    const hasLozing = existingLozingen.some((l: LozingenInputDto) => l.betrekkingTotTeeltwijze === teeltwijze);

    if (hasLozing) {
      setLozingenList(existingLozingen);
    } else {
      const newLozingenInputDto: LozingenInputDto = {
        betrekkingTotTeeltwijze: teeltwijze,
        lozingen: Array.from({ length: 13 }, (_, index) => ({
          periode: index + 1,
          hoeveelheid: undefined,
        })),
      };
      const updatedLozingen = [...existingLozingen, newLozingenInputDto];

      form.setFieldsValue({
        lozingen: updatedLozingen,
      });

      setLozingenList(updatedLozingen);
    }
  }, [teeltwijze, form]);

  const currentLozingIndex = lozingenList.findIndex(l => l.betrekkingTotTeeltwijze === teeltwijze);

  const data = useMemo(() => Array.from({ length: 13 }, (_, i) => ({ key: i, periode: i + 1 })), []);

  const listCols = teeltwijze === 0 ? { periode: 2, hoeveelheid: 2, voedingswater: 2, hergebruikt: 2 } : { periode: 2, hoeveelheid: 4 };

  return (
    <>
      <Title style={{ margin: '10px 0px' }} level={5}>
        <Conditional condition={teeltwijze === 0}>{t('form.emissionReport.volumes.titleGrondteelt')}</Conditional>
        <Conditional condition={teeltwijze === 1}>{t('form.emissionReport.volumes.titleSubstraatteelt')}</Conditional>
      </Title>
      {ground && <GroundWaterInfo />}
      <Typography style={{ marginBottom: '10px' }}>
        <Conditional condition={teeltwijze === 0}>{t('form.emissionReport.volumes.typeGrondteelt')}</Conditional>
        <Conditional condition={teeltwijze === 1}>{t('form.emissionReport.volumes.typeSubstraatteelt')}</Conditional>
      </Typography>
      <Conditional condition={currentLozingIndex !== -1}>
        <VolumeTableContainer usesRoutes={usesRoutes}>
          <Form.Item name={['lozingen', currentLozingIndex, 'betrekkingTotTeeltwijze']} initialValue={teeltwijze} hidden>
            <Input />
          </Form.Item>

          <Row style={{ fontWeight: 'bold', marginBottom: 4 }}>
            <Col span={listCols.periode} style={{ minWidth: 80 }}>
              {t('form.emissionReport.metingen.headers.period')}
            </Col>
            {volumeColumns.map(column => (
              <Col key={column.field} span={listCols.hoeveelheid} style={{ minWidth: usesRoutes ? (column.field === 'hoeveelheidRiool' ? 180 : 240) : 160 }}>
                {column.title}
                {volumeUnit}
              </Col>
            ))}
            <Conditional condition={teeltwijze === 0}>
              <>
                <Col span={listCols.voedingswater} style={{ minWidth: groundColumnWidth }}>
                  Toegediend voedingswater{volumeUnit}
                </Col>
                <Col span={listCols.hergebruikt} style={{ minWidth: groundColumnWidth }}>
                  Hergebruikt drainagewater{volumeUnit}
                </Col>
              </>
            </Conditional>
          </Row>
          {data.map(({ key, periode }, index) => (
            <Form.Item key={key} noStyle shouldUpdate={(prev, curr) => prev !== curr}>
              {() => (
                <Row className={styles.rowBorder} style={ground || !usesRoutes ? { padding: 4, height: 'auto', minHeight: 40 } : { padding: 4 }}>
                  <Col span={listCols.periode} style={{ minWidth: 80 }}>
                    <Form.Item
                      name={['lozingen', currentLozingIndex, 'lozingen', index, 'periode']}
                      initialValue={periode}
                      style={usesRoutes ? undefined : { marginBottom: 0 }}>
                      <span>{periode}</span>
                    </Form.Item>
                  </Col>
                  {volumeColumns.map(column => (
                    <Col
                      key={column.field}
                      span={listCols.hoeveelheid}
                      style={{ minWidth: usesRoutes ? (column.field === 'hoeveelheidRiool' ? 180 : 240) : 160 }}>
                      <Form.Item
                        name={['lozingen', currentLozingIndex, 'lozingen', index, column.field]}
                        dependencies={dependencies}
                        help={ground ? false : undefined}
                        validateTrigger={ground ? 'onBlur' : 'onChange'}
                        style={{ marginBottom: 0 }}
                        rules={[{ required: true, message: '' }, { validator: dischargeValidator }]}>
                        <InputNumber
                          min={0}
                          decimalSeparator={','}
                          precision={0}
                          step={1}
                          max={99999999.9}
                          style={{ width: '90%' }}
                          onBlur={() => onDischargeBlur(currentLozingIndex, index, column.field)}
                        />
                      </Form.Item>
                    </Col>
                  ))}
                  <Conditional condition={teeltwijze === 0}>
                    <>
                      <Col span={listCols.voedingswater} style={{ minWidth: groundColumnWidth }}>
                        <Form.Item
                          name={['lozingen', currentLozingIndex, 'lozingen', index, 'toegediendVoedingswaterHoeveelheid']}
                          rules={[{ required: ground, message: t('form.emissionReport.volumes.required') }]}
                          help={ground ? false : undefined}
                          style={usesRoutes ? undefined : { marginBottom: 0 }}>
                          <InputNumber min={0} decimalSeparator={','} precision={0} step={1} max={99999999.9} style={{ width: '90%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={listCols.hergebruikt} style={{ minWidth: groundColumnWidth }}>
                        <Form.Item
                          name={['lozingen', currentLozingIndex, 'lozingen', index, 'hergebruiktDrainagewaterHoeveelheid']}
                          dependencies={dependencies}
                          help={ground ? false : undefined}
                          validateTrigger={ground ? 'onBlur' : 'onChange'}
                          rules={[{ required: ground, message: t('form.emissionReport.volumes.required') }, { validator: reuseValidator }]}
                          style={{ marginBottom: 0 }}>
                          <InputNumber
                            min={0}
                            decimalSeparator={','}
                            precision={0}
                            step={1}
                            max={99999999.9}
                            style={{ width: '90%' }}
                            onBlur={() => onReuseBlur(currentLozingIndex, index)}
                          />
                        </Form.Item>
                      </Col>
                      {ground && (
                        <Col flex="1" style={{ minWidth: 200, paddingLeft: 8 }} role="alert">
                          <Form.Item noStyle shouldUpdate>
                            {() => (
                              <Form.ErrorList
                                errors={[
                                  ...new Set(
                                    [
                                      ...volumeColumns.map(column => column.field),
                                      'toegediendVoedingswaterHoeveelheid' as const,
                                      'hergebruiktDrainagewaterHoeveelheid' as const,
                                    ].flatMap(field => form.getFieldError(['lozingen', currentLozingIndex, 'lozingen', index, field]))
                                  ),
                                ]}
                              />
                            )}
                          </Form.Item>
                        </Col>
                      )}
                    </>
                  </Conditional>
                </Row>
              )}
            </Form.Item>
          ))}
        </VolumeTableContainer>
      </Conditional>
    </>
  );
};
